import {
  AccessibilityIcon,
  BarChart3Icon,
  BoxesIcon,
  ClipboardListIcon,
  CpuIcon,
  FrameIcon,
  GraduationCapIcon,
  LandmarkIcon,
  ShieldIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  description: string;
  result: string;
  pages: number;
  icon: LucideIcon;
  tags: string;
};

export const modules: Module[] = [
  {
    number: '01',
    title: 'Fundamentos de la Museología',
    subtitle: 'Comprender qué es un museo en la sociedad contemporánea',
    description:
      'Concepto de museo, museología vs museografía, evolución histórica, funciones, tipologías, patrimonio, colecciones, investigación, conservación, educación, comunicación, ética museológica y nuevos modelos de museo. Todo el contexto que necesitás para hablar con autoridad desde el primer día.',
    result:
      'Tus alumnos entienden por qué el museo es un centro cultural imprescindible, qué dice la normativa internacional sobre el rol de la institución, cuál es la responsabilidad del museólogo en la formación de públicos y ciudadanía, y por qué cada museo existe para garantizar igualdad de acceso al patrimonio y la cultura.',
    pages: 73,
    icon: LandmarkIcon,
    tags: 'CONCEPTO DE MUSEO · MUSEOLOGÍA VS MUSEOGRAFÍA · EVOLUCIÓN HISTÓRICA · TIPOLOGÍAS · ÉTICA MUSEOLÓGICA · NUEVOS MODELOS DE MUSEO',
  },
  {
    number: '02',
    title: 'Patrimonio y Gestión de Colecciones',
    subtitle: 'De los bienes culturales a una colección con sentido',
    description:
      'Patrimonio material e inmaterial, memoria e identidad, valor patrimonial, formación de colecciones, políticas de adquisición, donaciones, procedencia, investigación, responsabilidad institucional y ética de las colecciones. Con casos y contextos argentinos específicos.',
    result:
      'Tus alumnos diseñan políticas de colección fundamentadas, eligen bienes con criterios profesionales, gestionan procedencias correctamente, crean sistemas de investigación coherentes y comprenden el ciclo completo de la gestión de patrimonio desde la identificación hasta la preservación.',
    pages: 89,
    icon: BoxesIcon,
    tags: 'PATRIMONIO MATERIAL E INMATERIAL · MEMORIA E IDENTIDAD · POLÍTICAS DE ADQUISICIÓN · PROCEDENCIA · ÉTICA DE LAS COLECCIONES',
  },
  {
    number: '03',
    title: 'Documentación, Inventario y Catalogación',
    subtitle: 'Registrar para conocer, controlar y preservar',
    description:
      'Inventario, catalogación, numeración, fichas descriptivas, procedencia, registro fotográfico, bases de datos, normalización, trazabilidad, digitalización documental y gestión de información museológica. Sistema completo para garantizar control y preservación.',
    result:
      'Tus alumnos documentan colecciones siguiendo estándares internacionales, diseñan sistemas de organización coherentes y accesibles, gestionan bases de datos museológicas profesionales y comprenden cómo la documentación facilita investigación, conservación y acceso público.',
    pages: 107,
    icon: ClipboardListIcon,
    tags: 'INVENTARIO · FICHAS DESCRIPTIVAS · NUMERACIÓN · REGISTRO FOTOGRÁFICO · BASES DE DATOS · NORMALIZACIÓN · TRAZABILIDAD · DIGITALIZACIÓN',
  },
  {
    number: '04',
    title: 'Conservación Preventiva y Gestión de Riesgos',
    subtitle: 'Proteger las colecciones antes de que aparezca el daño',
    description:
      'Agentes de deterioro, temperatura, humedad, iluminación, plagas, manipulación, depósitos, embalaje, materiales, emergencias, monitoreo, sensores y sostenibilidad. Con diagnóstico integral de colecciones en riesgo.',
    result:
      'Tus alumnos diseñan planes de conservación preventiva, identifican riesgos, crean protocolos de manipulación seguros, monitorean condiciones ambientales y comprenden que la conservación preventiva es mil veces más efectiva que restaurar lo que ya se dañó.',
    pages: 96,
    icon: ShieldIcon,
    tags: 'AGENTES DE DETERIORO · TEMPERATURA Y HUMEDAD · ILUMINACIÓN · PLAGAS · MANIPULACIÓN · DEPÓSITOS · EMERGENCIAS · MONITOREO',
  },
  {
    number: '05',
    title: 'Museografía y Diseño de Exposiciones',
    subtitle: 'Transformar una idea en una experiencia expositiva',
    description:
      'Guion museológico y museográfico, tipos de exposiciones, recorridos, diseño espacial, vitrinas, iluminación, textos de sala, cartelería, accesibilidad, tecnología, producción y montaje. Exposiciones físicas, escolares, comunitarias, digitales e híbridas. Con diseño completo de exposición.',
    result:
      'Tus alumnos diseñan exposiciones que comunican, crean recorridos que emocionan, escriben textos que enganchan, aplican tecnología con propósito y comprenden que una exposición bien diseñada puede cambiar la forma en que la gente ve el mundo.',
    pages: 100,
    icon: FrameIcon,
    tags: 'GUION MUSEOGRÁFICO · RECORRIDOS · DISEÑO ESPACIAL · VITRINAS · ILUMINACIÓN · TEXTOS DE SALA · MONTAJE · EXPOSICIONES HÍBRIDAS',
  },
  {
    number: '06',
    title: 'Educación en Museos y Mediación Cultural',
    subtitle: 'Convertir el museo en un espacio de aprendizaje',
    description:
      'Museo como aula viva, mediación cultural, públicos educativos, visitas guiadas, aprendizaje mediante objetos, talleres, secuencias didácticas, mediación para públicos diversos, educación comunitaria, territorio, memoria, gamificación y recursos híbridos. Con Proyecto Educativo Museístico 2026.',
    result:
      'Tus alumnos diseñan programas educativos potentes, median encuentros entre personas y objetos que transforman, crean espacios donde el aprendizaje es deseo y no obligación, y comprenden que la educación es la función más transformadora de un museo.',
    pages: 93,
    icon: GraduationCapIcon,
    tags: 'MEDIACIÓN CULTURAL · VISITAS GUIADAS · APRENDIZAJE CON OBJETOS · TALLERES · SECUENCIAS DIDÁCTICAS · EDUCACIÓN COMUNITARIA · GAMIFICACIÓN',
  },
  {
    number: '07',
    title: 'Gestión y Administración de Museos',
    subtitle: 'Organizar una institución museística de forma sostenible',
    description:
      'Misión, visión, objetivos, planificación, organización institucional, equipos, roles, presupuesto, financiamiento, comunicación, redes e indicadores. Con PLAN ANUAL DE GESTIÓN MUSEÍSTICA 2026 completo. Especial atención a equipos pequeños y presupuestos limitados.',
    result:
      'Tus alumnos formulan planes estratégicos con visión clara, diseñan presupuestos realistas, crean sistemas de evaluación basados en indicadores concretos y comprenden que la gestión eficiente es el corazón que permite que todo lo demás funcione.',
    pages: 60,
    icon: BarChart3Icon,
    tags: 'MISIÓN Y VISIÓN · PLANIFICACIÓN · EQUIPOS Y ROLES · PRESUPUESTO · FINANCIAMIENTO · COMUNICACIÓN · INDICADORES · PLAN ANUAL 2026',
  },
  {
    number: '08',
    title: 'Públicos, Comunidad, Accesibilidad e Inclusión',
    subtitle: 'Diseñar museos que realmente puedan ser habitados por todos',
    description:
      'Estudios de públicos, segmentación, experiencia del visitante, barreras, accesibilidad física, sensorial, cognitiva y digital, inclusión, diversidad cultural, territorio, comunidad, participación y co-creación. Con el desafío: CONSTRUIR UN MUSEO PARA TODOS.',
    result:
      'Tus alumnos diseñan espacios para todos, entienden que la accesibilidad es un derecho y no una concesión, crean experiencias inclusivas genuinas y comprenden que un museo que no incluye es un museo que fracasa en su función fundamental.',
    pages: 76,
    icon: AccessibilityIcon,
    tags: 'ESTUDIOS DE PÚBLICOS · EXPERIENCIA DEL VISITANTE · ACCESIBILIDAD FÍSICA Y SENSORIAL · DIVERSIDAD CULTURAL · PARTICIPACIÓN · CO-CREACIÓN',
  },
  {
    number: '09',
    title: 'Innovación, Tecnología y Museos del Futuro',
    subtitle: 'Preparar al museo para 2026 y los próximos años',
    description:
      'Transformación digital, digitalización del patrimonio, colecciones digitales, museos virtuales, experiencias híbridas, tecnologías interactivas, IA, patrimonio digital, narrativas transmedia, datos, analítica, sostenibilidad y ética tecnológica. Con PLAN DE INNOVACIÓN MUSEÍSTICA 2026.',
    result:
      'Tus alumnos entienden que la innovación no es gadgetería sino propósito, aprenden a aplicar tecnología con ética, crean experiencias que conectan lo físico con lo digital y comprenden que los museos del futuro serán híbridos, accesibles, sostenibles e inclusivos.',
    pages: 60,
    icon: CpuIcon,
    tags: 'TRANSFORMACIÓN DIGITAL · COLECCIONES DIGITALES · MUSEOS VIRTUALES · IA EN CURADURÍA · TRANSMEDIA · ANALÍTICA · ÉTICA TECNOLÓGICA',
  },
];
